import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import "./DevAuthGate.css";

export default function DevAuthGate({ loading, submitting, error, text, onSubmit, onResend, onVerify }) {
  const ar = text.login === "دخول" || /[\u0600-\u06ff]/.test(text.login);
  const copy = ar ? {
    title: "أنشئ حسابك في FRAMEANA", first: "الاسم الأول", last: "اسم العائلة", gender: "الجنس",
    male: "ذكر", female: "أنثى", dob: "تاريخ الميلاد", confirm: "تأكيد كلمة المرور",
    mismatch: "كلمتا المرور غير متطابقتين.", hint: "استخدم من 8 إلى 128 حرفًا.",
    saved: "تم إنشاء حسابك", pending: "بريدك الإلكتروني بانتظار التحقق",
    notice: "أرسلنا رابط تحقق إلى بريدك. افتح الرسالة لتفعيل حسابك. الرابط صالح لمدة 24 ساعة؛ تحقق من مجلد الرسائل غير المرغوب فيها أيضًا.",
    resend: "إعادة إرسال رسالة التحقق", back: "العودة إلى تسجيل الدخول",
  } : {
    title: "Create your FRAMEANA Account", first: "First Name", last: "Last Name", gender: "Gender",
    male: "Male", female: "Female", dob: "Date of Birth", confirm: "Confirm Password",
    mismatch: "Passwords do not match.", hint: "Use 8 to 128 characters.",
    saved: "Account Created", pending: "Your email is awaiting verification",
    notice: "We've sent a verification link to your email. Please verify your email before signing in. The link expires in 24 hours; check your spam folder too.",
    resend: "Resend Verification Email", back: "Back to Sign In",
  };
  const [mode, setMode] = useState("login");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [localError, setLocalError] = useState("");
  const [mailSent, setMailSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [verification, setVerification] = useState(() => window.location.pathname.endsWith("/verify-email") ? "checking" : "");
  const verificationStarted = useRef(false);
  useEffect(() => {
    if (verification !== "checking" || verificationStarted.current) return;
    verificationStarted.current = true;
    const token = new URLSearchParams(window.location.search).get("token") || "";
    window.history.replaceState({}, "", window.location.pathname);
    Promise.resolve().then(() => onVerify(token)).then(() => setVerification("verified")).catch(() => setVerification("invalid"));
  }, [verification, onVerify]);
  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);
  async function resend(email) {
    if (resending || cooldown) return;
    setResending(true); setResendMessage("");
    try {
      await onResend(email);
      setCooldown(60);
      setResendMessage(ar ? "إذا كان الحساب يحتاج إلى التحقق فستصلك رسالة. انتظر دقيقة قبل إعادة الطلب." : "If the account needs verification, a link will be sent. Please wait a minute before requesting another.");
    } catch (failure) { setResendMessage(failure.message); }
    finally { setResending(false); }
  }
  const busy = useRef(false);
  const register = mode === "register";

  async function submit(event) {
    event.preventDefault();
    if (busy.current) return;
    const form = event.currentTarget;
    const fields = Object.fromEntries(new FormData(form));
    setLocalError("");
    if (register && fields.password !== fields.confirm_password) {
      setLocalError(copy.mismatch);
      form.elements.confirm_password.focus();
      return;
    }
    busy.current = true;
    try {
      const result = await onSubmit({ mode, ...fields });
      if (register && result?.user) {
        form.reset();
        setRegisteredEmail(result.user.email || fields.email);
        setMailSent(result.verification_email_sent === true);
        setCooldown(60);
      }
    } finally { busy.current = false; }
  }

  const field = (name, label, type = "text", props = {}) => (
    <label className="auth-field" key={name}>
      <span>{label}</span>
      <div className="auth-input-shell"><input name={name} type={type} required {...props} /></div>
    </label>
  );

  if (verification) return <section className="auth-form f2-confirmation" style={{ margin: "64px auto" }} role="status">
    <ShieldCheck size={40} /><h2>{verification === "checking" ? (ar ? "جارٍ التحقق…" : "Verifying email…") : verification === "verified" ? (ar ? "تم توثيق البريد ✓" : "Email Verified ✓") : (ar ? "الرابط غير صالح أو منتهي الصلاحية" : "Invalid or expired verification link")}</h2>
    {verification === "verified" && <p>{ar ? "حساب FRAMEANA أصبح مفعّلًا." : "Your FRAMEANA account is now active."}</p>}
    {verification === "invalid" && <p>{ar ? "اطلب رابطًا جديدًا من تبويب إنشاء الحساب." : "Request a new link from the Create Account tab."}</p>}
    {verification !== "checking" && <a className="btn btn-primary" href={`${process.env.PUBLIC_URL || ""}/`}>{text.signIn}</a>}
  </section>;
  if (loading) return <div className="loading-state" role="status"><Loader2 className="spin-icon" /><h2>{text.authVerifying}</h2></div>;

  return <section className="auth-gate f2-auth" dir={ar ? "rtl" : "ltr"}>
    <div className="auth-intro">
      <div className="auth-wordmark">FRAMEANA</div><span className="auth-rule" />
      <h1>{text.authHeroTitle}</h1><p>{text.authStudioSubtitle}</p>
      <ShieldCheck size={64} strokeWidth={1} aria-hidden="true" />
    </div>
    {registeredEmail ? <div className="auth-form f2-confirmation" role="status" aria-live="polite">
      <Mail size={40} aria-hidden="true" /><h2>{copy.saved}</h2><h3>{copy.pending}</h3>
      <strong className="f2-email" dir="ltr">{registeredEmail}</strong><p id="f2-mail-notice">{mailSent ? copy.notice : (ar ? "تم حفظ الحساب لكن تعذر إرسال الرسالة. أعد المحاولة بعد دقيقة." : "Your account was saved, but the email could not be sent. Please retry in a minute.")}</p>
      <button className="btn btn-primary" disabled={resending || cooldown > 0} onClick={() => resend(registeredEmail)} aria-describedby="f2-mail-notice">{copy.resend}{cooldown > 0 ? ` (${cooldown}s)` : ""}</button>
      {resendMessage && <p role="status">{resendMessage}</p>}
      <button className="btn" onClick={() => { setRegisteredEmail(""); setMode("login"); }}>{copy.back}</button>
    </div> : <form className="auth-form" onSubmit={submit}>
      <div className="auth-form-brand"><h2>{register ? copy.title : text.authLoginTitle}</h2></div>
      <div className="auth-mode-toggle" role="group" aria-label={text.authModeLabel}>
        {["login", "register"].map(value => <button type="button" key={value} disabled={submitting} aria-pressed={mode === value} className={mode === value ? "auth-mode-active" : ""} onClick={() => { setMode(value); setLocalError(""); }}>{text[value]}</button>)}
      </div>
      <fieldset className="f2-fields" disabled={submitting} key={mode}>
        {register && <div className="f2-names">{field("first_name", copy.first, "text", { autoComplete: "given-name", maxLength: 120, pattern: ".*\\S.*" })}{field("last_name", copy.last, "text", { autoComplete: "family-name", maxLength: 120, pattern: ".*\\S.*" })}</div>}
        {field("email", text.email, "email", { autoComplete: "email", maxLength: 254, dir: "ltr" })}
        {register && <>
          <fieldset className="f2-gender"><legend>{copy.gender}</legend>{["male", "female"].map(value => <label key={value}><input type="radio" name="gender" value={value} required />{copy[value]}</label>)}</fieldset>
          {field("date_of_birth", copy.dob, "date", { autoComplete: "bday", min: "0001-01-01", max: new Date().toISOString().slice(0, 10) })}
        </>}
        {field("password", text.password, "password", { autoComplete: register ? "new-password" : "current-password", minLength: register ? 8 : 6, ...(register ? { maxLength: 128, "aria-describedby": "f2-password-hint" } : {}) })}
        {register && <><small id="f2-password-hint">{copy.hint}</small>{field("confirm_password", copy.confirm, "password", { autoComplete: "new-password", minLength: 8, maxLength: 128 })}</>}
      </fieldset>
      {(localError || error) && <div className="inline-alert auth-alert" role="alert">{localError || error}</div>}
      {register && <><button type="button" className="btn" disabled={resending || cooldown > 0 || submitting} onClick={event => {
        const input = event.currentTarget.form.elements.email;
        if (!input.reportValidity()) return;
        resend(input.value);
      }}>{copy.resend}{cooldown > 0 ? ` (${cooldown}s)` : ""}</button>{resendMessage && <p role="status">{resendMessage}</p>}</>}
      <button type="submit" className="btn btn-primary auth-submit" disabled={submitting}>{submitting && <Loader2 size={18} className="spin-icon" />}{register ? text.createAccount : text.signIn}</button>
    </form>}
  </section>;
}
